import { useState, useEffect } from "react";
import { P, FF } from "../../styles/tokens";
import { api } from "../../services/api";
import { useSubscription } from "../../hooks/useSubscription";
import { useToast } from "../../contexts/ToastContext";

/* ── localStorage helper para dismiss con expiración de 24h ── */
const DISMISS_KEY = "arbo_trial_banner_dismissed";
const DISMISS_TTL_MS = 24 * 60 * 60 * 1000; // 24 horas

function isDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const { ts } = JSON.parse(raw);
    if (Date.now() - ts > DISMISS_TTL_MS) {
      localStorage.removeItem(DISMISS_KEY);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

function setDismissed() {
  try {
    localStorage.setItem(DISMISS_KEY, JSON.stringify({ ts: Date.now() }));
  } catch {}
}

/* ── Calcular días restantes ── */
function daysLeft(periodEnd) {
  if (!periodEnd) return null;
  const diff = new Date(periodEnd) - new Date();
  if (diff <= 0) return 0;
  return Math.ceil(diff / 86400000);
}

/**
 * Banner persistente que muestra el estado del trial.
 * Se monta globalmente (App.jsx) encima del contenido principal.
 * Se oculta automáticamente si no hay trial activo, y guarda dismiss 24h.
 */
export default function TrialBanner() {
  const { subscription, loading } = useSubscription();
  const { show: showToast } = useToast();
  const [dismissed, setDismissedState] = useState(isDismissed);
  const [working, setWorking] = useState(false);

  // Re-chequear dismissed si cambia la suscripción (ej. tras activar trial)
  useEffect(() => {
    setDismissedState(isDismissed());
  }, [subscription]);

  if (loading || !subscription || dismissed) return null;

  const isTrial = subscription.is_trial === true;
  const periodEnd = subscription.current_period_end;
  const remaining = daysLeft(periodEnd);
  const trialExpired = !isTrial && remaining !== null && remaining <= 0;

  // Detectar trial vencido: plan "gratis" + had trial_used_at (volvió a free)
  const pendingExpired =
    !isTrial &&
    subscription.plan === "gratis" &&
    subscription.trial_used_at != null &&
    trialExpired;

  // Solo mostrar si hay trial activo o recién vencido
  const shouldShow = (isTrial && remaining !== null && remaining > 0) || pendingExpired;
  if (!shouldShow) return null;

  const planLabel =
    subscription.plan === "pro" ? "Pro" : subscription.plan === "basico" ? "Básico" : "";

  async function handleSubscribe() {
    setWorking(true);
    try {
      const plan = pendingExpired ? "pro" : (subscription.plan || "pro");
      const { checkout_url } = await api.subscriptions.checkout(plan);
      window.location.href = checkout_url;
    } catch (e) {
      showToast("Error al iniciar pago: " + (e?.message || "intenta de nuevo"));
      setWorking(false);
    }
  }

  function handleDismiss() {
    setDismissed();
    setDismissedState(true);
  }

  return (
    <div
      role="alert"
      style={{
        background: P.warn.pastel,
        borderBottom: `1px solid ${P.warn.tint}`,
        color: P.warn.text,
        padding: "10px 16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        flexWrap: "wrap",
        fontFamily: FF,
        fontSize: 13,
        fontWeight: 600,
        position: "relative",
        zIndex: 40,
      }}
    >
      <span>
        {pendingExpired
          ? "Tu trial venció. Volviste a Gratis"
          : `Tu trial ${planLabel} vence en ${remaining} día${remaining === 1 ? "" : "s"}`}
      </span>

      <button
        onClick={handleSubscribe}
        disabled={working}
        style={{
          padding: "5px 14px",
          borderRadius: 8,
          border: `1.5px solid ${P.warn.text}`,
          background: P.warn.text,
          color: "#fff",
          fontFamily: FF,
          fontSize: 12,
          fontWeight: 700,
          cursor: working ? "default" : "pointer",
          opacity: working ? 0.7 : 1,
          whiteSpace: "nowrap",
          transition: "opacity .15s",
        }}
      >
        {working ? "Cargando..." : pendingExpired ? "Reactivar Pro" : "Suscribirse"}
      </button>

      {/* Botón X para descartar */}
      <button
        onClick={handleDismiss}
        aria-label="Cerrar banner"
        style={{
          position: "absolute",
          right: 12,
          top: "50%",
          transform: "translateY(-50%)",
          background: "none",
          border: "none",
          cursor: "pointer",
          color: P.warn.text,
          fontFamily: FF,
          fontSize: 16,
          fontWeight: 700,
          lineHeight: 1,
          padding: "4px 6px",
          opacity: 0.7,
        }}
      >
        ×
      </button>
    </div>
  );
}
